import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../../contexts/AuthContext';
import { deleteEvent } from '../../../../services/eventService';
import { deleteSetlist } from '../../../../services/setlistService';

/**
 * Reusable button component for deleting events with permission checking
 * 
 * @param {Object} props
 * @param {Object} props.event - The event object to delete
 * @param {Function} props.onSuccess - Callback function to run after successful deletion
 * @param {Function} props.onError - Callback function to handle errors
 * @param {string} props.buttonStyle - Additional CSS class for the button
 * @param {string} props.iconClass - CSS class for the icon
 * @param {string} props.buttonText - Optional text to display next to the icon
 * @param {boolean} props.showConfirmation - Whether to show a confirmation dialog (default: true)
 */
function DeleteEventButton({
  event,
  onSuccess,
  onError,
  buttonStyle = "btn-link text-danger",
  iconClass = "",
  buttonText = "",
  showConfirmation = true
}) {
  const { user } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if user can delete this event
  const canDeleteEvent = () => {
    if (!user || !event) return false;
    
    // User can delete if they are the owner
    if (user.id === event.owner_id) return true;
    
    // User can delete if they are an admin of the event's community
    if (event.community_id && user.isAdminOfCommunity && user.isAdminOfCommunity(event.community_id)) {
      return true;
    }
    
    // User can delete if they are a global admin
    if (user.is_global_admin) return true;
    
    return false;
  };

  // Handle delete button click
  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Don't proceed if already deleting or no event data
    if (isDeleting || !event?.id) return;
    
    // Show confirmation dialog if configured
    if (showConfirmation) {
      const confirmDelete = window.confirm(
        "Ali ste prepričani, da želite izbrisati dogodek? Te akcije ni mogoče razveljaviti."
      );
      if (!confirmDelete) return;
    }
    
    setIsDeleting(true);
    
    try {
      // Delete the event
      await deleteEvent(event.id);
      
      // If the event had a setlist, delete that too to prevent orphaned data
      if (event.setlist_id) {
        try {
          await deleteSetlist(event.setlist_id);
        } catch (setlistError) {
          // Continue even if setlist deletion fails
        }
      }
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess(event.id);
      }
    } catch (error) {
      
      // Call error callback if provided
      if (onError) {
        onError(error.message || "Napaka pri brisanju dogodka");
      }
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Only render the button if user has permission to delete
  if (!canDeleteEvent()) {
    return null;
  }
  
  return (
    <button 
      className={`btn ${buttonStyle}`}
      onClick={handleDelete}
      disabled={isDeleting}
      title="Izbriši dogodek"
    >
      {isDeleting ? (
        <FontAwesomeIcon icon={faSpinner} spin className={iconClass} />
      ) : (
        <FontAwesomeIcon icon={faTrash} className={iconClass} />
      )}
      {buttonText && <span className="ms-1">{buttonText}</span>}
    </button>
  );
}

export default DeleteEventButton;